/** @format */

export type ReleaseEntryKind = "feature" | "fix" | "improvement";

export interface ReleaseEntry {
	kind: ReleaseEntryKind;
	text: string;
	scope?: string;
}

export interface Release {
	version: string;
	date: string;
	entries: ReleaseEntry[];
}

const releaseHeadingPattern = /^#{1,2}\s+\[?(\d+\.\d+\.\d+)\]?.*\((\d{4}-\d{2}-\d{2})\)\s*$/;

const trailingCommitLinkPattern = /\s*\(\[[0-9a-f]{7,40}\]\(.*\)\)$/;

const leadingScopePattern = /^\*\*(.+?):\*\*\s*/;

const bulletPattern = /^\*\s+(.*)$/;

const sectionKindByHeading: Record<string, ReleaseEntryKind | undefined> = {
	"### Features": "feature",
	"### Bug Fixes": "fix",
	"### Performance Improvements": "improvement",
};

const parseEntry = (bulletText: string, kind: ReleaseEntryKind): ReleaseEntry => {
	const text = bulletText.trim().replace(trailingCommitLinkPattern, "");
	const scopeMatch = text.match(leadingScopePattern);
	if (scopeMatch === null) {
		return { kind, text };
	}
	return { kind, text: text.slice(scopeMatch[0].length), scope: scopeMatch[1] };
};

export const parseChangelog = (raw: string): Release[] => {
	const releases: Release[] = [];
	let currentRelease: Release | null = null;
	let currentKind: ReleaseEntryKind | null = null;

	for (const line of raw.split("\n")) {
		const headingMatch = line.match(releaseHeadingPattern);
		if (headingMatch !== null) {
			currentRelease = { version: headingMatch[1], date: headingMatch[2], entries: [] };
			currentKind = null;
			releases.push(currentRelease);
			continue;
		}

		if (line.startsWith("### ")) {
			currentKind = sectionKindByHeading[line.trim()] ?? null;
			continue;
		}

		const bulletMatch = line.match(bulletPattern);
		if (bulletMatch === null || currentRelease === null || currentKind === null) {
			continue;
		}
		currentRelease.entries.push(parseEntry(bulletMatch[1], currentKind));
	}

	return releases;
};

export const compareVersions = (a: string, b: string): number => {
	const partsA = a.split(".").map(Number);
	const partsB = b.split(".").map(Number);
	const partCount = Math.max(partsA.length, partsB.length);

	for (let i = 0; i < partCount; i++) {
		const difference = (partsA[i] ?? 0) - (partsB[i] ?? 0);
		if (difference !== 0) {
			return difference;
		}
	}
	return 0;
};
